import Markdown from "markdown-it";
import highlight from "highlight.js";
import DOMPurify from "dompurify";

const ALLOWED_TAGS = [
  "a",
  "b",
  "blockquote",
  "br",
  "caption",
  "code",
  "col",
  "colgroup",
  "del",
  "div",
  "em",
  "figcaption",
  "figure",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "i",
  "img",
  "li",
  "ol",
  "p",
  "pre",
  "span",
  "strong",
  "sub",
  "sup",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "u",
  "ul",
];

const ALLOWED_ATTRIBUTES = [
  "align",
  "alt",
  "border",
  "class",
  "colspan",
  "height",
  "href",
  "rel",
  "rowspan",
  "src",
  "style",
  "target",
  "title",
  "width",
];

const HTML_TAG_RE =
  /<\/?(?:p|div|span|img|table|thead|tbody|tfoot|tr|td|th|ul|ol|li|h[1-6]|br|hr|strong|em|b|i|u|a|blockquote|pre|code|section|article|figure|figcaption|caption|sub|sup)\b/i;

const ESCAPED_HTML_TAG_RE =
  /&lt;\/?(?:p|div|span|img|table|thead|tbody|tfoot|tr|td|th|ul|ol|li|h[1-6]|br|hr|strong|em|b|i|u|a|blockquote|pre|code|section|article|figure|figcaption|caption|sub|sup)\b/i;

const embedMarkdown = new Markdown({
  html: true,
  linkify: true,
  typographer: false,
  breaks: true,
  langPrefix: "language-",
  highlight(code, lang) {
    if (lang && highlight.getLanguage(lang)) {
      try {
        return `<pre class="code-block"><code>${
          highlight.highlight(lang, code, true).value
        }</code></pre>`;
      } catch (error) {
        return "";
      }
    }
    return "";
  },
});

// 避免后端 HTML 因缩进被当成代码块，从而把标签转义成可见文本
embedMarkdown.disable("code");

const defaultLinkOpen =
  embedMarkdown.renderer.rules.link_open ||
  ((tokens, index, options, env, renderer) => renderer.renderToken(tokens, index, options));

embedMarkdown.renderer.rules.link_open = (tokens, index, options, env, renderer) => {
  tokens[index].attrSet("target", "_blank");
  tokens[index].attrSet("rel", "noopener noreferrer");
  return defaultLinkOpen(tokens, index, options, env, renderer);
};

embedMarkdown.renderer.rules.table_open = () => '<div class="table-scroll"><table>';
embedMarkdown.renderer.rules.table_close = () => "</table></div>";

function decodeEscapedHtml(source) {
  if (!source || !ESCAPED_HTML_TAG_RE.test(source)) {
    return source;
  }
  const textarea = document.createElement("textarea");
  textarea.innerHTML = source;
  return textarea.value;
}

function shouldRenderAsHtml(source) {
  const trimmed = source.trim();
  return trimmed.startsWith("<") && HTML_TAG_RE.test(trimmed);
}

function wrapPlainTables(container) {
  container.querySelectorAll("table").forEach((table) => {
    if (table.parentElement && table.parentElement.classList.contains("table-scroll")) {
      return;
    }
    const wrapper = document.createElement("div");
    wrapper.className = "table-scroll";
    table.parentNode.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });
}

export function renderEmbedMarkdown(source) {
  const text = decodeEscapedHtml(source || "");
  const rendered = shouldRenderAsHtml(text) ? text : embedMarkdown.render(text);
  const sanitizedHtml = DOMPurify.sanitize(rendered, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ALLOWED_ATTRIBUTES,
    ALLOW_DATA_ATTR: false,
  });
  const container = document.createElement("div");
  container.innerHTML = sanitizedHtml;

  container.querySelectorAll("a").forEach((link) => {
    if (!link.textContent.trim() && !link.querySelector("img")) {
      link.parentNode.removeChild(link);
      return;
    }
    if (link.hasAttribute("href")) {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    } else {
      const parent = link.parentNode;
      while (link.firstChild) {
        parent.insertBefore(link.firstChild, link);
      }
      parent.removeChild(link);
    }
  });

  wrapPlainTables(container);

  return container.innerHTML;
}

export default embedMarkdown;
